import { useState, useMemo } from "react";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import { Copy, Check } from "lucide-react";

/**
 * Renders a fenced code block (```lang\ncode\n```) with syntax highlighting.
 * Supports a copy-to-clipboard button and a language label.
 *
 * Languages auto-detected from the fence info string (```apex, ```java, etc).
 * Falls back to "text" when no language is provided.
 */
export default function CodeBlock({ language, value }) {
  const [copied, setCopied] = useState(false);

  // Normalize language aliases: people often write "js" for "javascript",
  // and the AI emits "soql" / "sosl" which Prism understands as "sql".
  const lang = useMemo(() => normalizeLang(language), [language]);
  const label = (language || "text").toLowerCase();

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — silently ignore */
    }
  };

  return (
    <div
      className="my-3 rounded-lg overflow-hidden border border-zinc-700 bg-[#282c34] not-prose"
      data-testid="chat-code-block"
    >
      <div className="flex items-center justify-between px-3 py-1.5 bg-zinc-800/80 border-b border-zinc-700">
        <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">
          {label}
        </span>
        <button
          type="button"
          onClick={copy}
          className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-zinc-300 hover:text-white px-2 py-0.5 rounded transition-colors"
          data-testid="chat-code-copy"
        >
          {copied ? (
            <>
              <Check className="w-3 h-3 text-emerald-400" /> copied
            </>
          ) : (
            <>
              <Copy className="w-3 h-3" /> copy
            </>
          )}
        </button>
      </div>
      <SyntaxHighlighter
        language={lang}
        style={oneDark}
        showLineNumbers
        customStyle={{
          margin: 0,
          padding: "12px 14px",
          fontSize: "12.5px",
          lineHeight: 1.55,
          background: "#282c34",
        }}
        lineNumberStyle={{ color: "#4a5159", minWidth: "1.6em", fontSize: "11px" }}
        codeTagProps={{ style: { fontFamily: 'ui-monospace,"SF Mono",Menlo,Consolas,monospace' } }}
      >
        {value || ""}
      </SyntaxHighlighter>
    </div>
  );
}

function normalizeLang(raw) {
  const k = (raw || "").toLowerCase().trim();
  if (!k) return "text";
  const map = {
    js: "javascript",
    ts: "typescript",
    jsx: "jsx",
    tsx: "tsx",
    py: "python",
    sh: "bash",
    shell: "bash",
    zsh: "bash",
    yml: "yaml",
    md: "markdown",
    soql: "sql",
    sosl: "sql",
    // Apex is Salesforce-specific — Prism doesn't ship it, but its syntax is
    // close to Java so we render it that way. Far better than no colors.
    apex: "java",
    cls: "java",
    trigger: "java",
    lwc: "javascript",
    html: "markup",
    xml: "markup",
    vue: "markup",
  };
  return map[k] || k;
}
